import React, { useEffect, useState } from 'react';
import classes from './VoiceRecorder.module.css';
import { AudioVisualizer } from '../AudioVisualizer/AudioVisualizer';
import PrimaryButtonV2 from '../Buttons/PrimaryButtonV2';

import CustomTextField from '../Inputs/CustomTextField';
import { MediaPlayer } from '../AudioPlayer/MediaPlayer';
import {
  fetchDocumentTypes,
  IDocumentInput,
  selectDocumentTypes,
} from '../../../store/slices/documents';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { IIdAndLabel } from '../Inputs/ModalDropdown';
import { v4 as uuidv4 } from 'uuid';
import useSpeechRecognition from '../../../hooks/useSpeechRecognition';
import { getSpeechLocale } from '../../../utils/dateLocale';
import useMediaRecorder from '../../../hooks/useMediaRecorder';
import CustomDropdown from '../Inputs/CustomDropdown';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  handleSave: (document: IDocumentInput) => void;
  documentTitle: string;
  setDocumentTitle: (value: string) => void;
  documentComments: string;
  setDocumentComments: (value: string) => void;
};

const VoiceRecorder: React.FC<Props> = ({
  handleSave,
  documentTitle,
  setDocumentTitle,
  documentComments,
  setDocumentComments,
}) => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const lang = getSpeechLocale(localStorage.getItem('lang') || 'en');
  const [selectedDocumentType, setSelectedDocumentType] = useState<number>();
  const documentTypes = useAppSelector(selectDocumentTypes);

  useEffect(() => {
    dispatch(fetchDocumentTypes());
  }, [dispatch]);

  const {
    startRecording,
    stopRecording,
    clearRecording,
    recordedBlob,
    recordedUrl,
    status,
    streamRef,
  } = useMediaRecorder('audio');

  const { transcript, startListening, stopListening, clearTranscript } = useSpeechRecognition(lang);

  const handleRecordingClick = () => {
    if (status === 'idle') {
      startListening();
      startRecording();
    } else if (status === 'recording') {
      stopListening();
      stopRecording();
    }
  };

  const handleDropDown = (value: string) => {
    setSelectedDocumentType(parseInt(value));
  };

  const handleSubmit = () => {
    if (!recordedBlob || !recordedUrl || !selectedDocumentType) {
      console.error('No recorded blob available');
      return;
    }
    const documentTitleWithPostfix = `${documentTitle}.${recordedBlob.type.split('/')[1]}`;
    const file = new File([recordedBlob], `${documentTitleWithPostfix}`, {
      type: recordedBlob.type,
    });

    handleSave({
      document: {
        id: uuidv4(),
        title: documentTitle,
        comment: documentComments,
        attachment_type_id: selectedDocumentType,
        attachment_type_description: documentTypes.find(
          (documentType) => documentType.attachment_type_id === selectedDocumentType
        )?.attachment_type_description,
        name: documentTitleWithPostfix,
        link: recordedUrl,
        attachment_url: '',
        datetime_created: new Date(),
        mime_type: recordedBlob.type,
        transcription_text: transcript ?? undefined,
        transcriptionLanguageId: lang === 'en-GB' ? 1 : lang === 'de-DE' ? 2 : undefined,
      },
      file: file,
    });
  };

  return (
    <div className={classes.container}>
      {status === 'idle' && (
        <div className={classes.visualizerContainer}>
          <div className={classes.visualizerImage} />
          <div className={classes.timer}>00:00</div>
        </div>
      )}
      {status === 'recording' && streamRef && <AudioVisualizer audioStream={streamRef.current} />}
      {recordedUrl && (
        <div className={classes.audioPlayerContainer}>
          <MediaPlayer mediaType="audio" url={recordedUrl} urlType="blob" />
        </div>
      )}
      {/* <div className={classes.transcriptionContainer}>
        <div className={classes.transcriptionTitle} style={transcript ? {} : { color: '#D9D9D9' }}>
          {translations('transcription')}
        </div>
        <textarea value={transcript} onChange={(e) => updatedTranscription(e.target.value)}>
          {transcript}
        </textarea>
      </div> */}
      {(status === 'idle' || status === 'recording') && (
        <div onClick={handleRecordingClick} className={classes.button}>
          {status === 'idle' ? translations('start.recording') : translations('stop.recording')}
        </div>
      )}
      {status === 'stopped' && (
        <>
          <div className={classes.textFields}>
            <CustomTextField
              label={translations('add.title').toString()}
              value={documentTitle}
              onChange={(e: any) => setDocumentTitle(e.target.value)}
            />
            <div style={{ marginBottom: '20px' }}></div>
            <CustomTextField
              label={translations('add.comments').toString()}
              value={documentComments}
              onChange={(e: any) => setDocumentComments(e.target.value)}
            />
            <div style={{ marginBottom: '20px' }}></div>
            <CustomDropdown
              dropdownStyle={{
                height: '48px',
                color: selectedDocumentType ? 'rgba(31, 31, 31, 1)' : '#AFB2C3',
                fontSize: 18,
                margin: '0px 3px',
              }}
              maxWidth={'100%'}
              label={translations('label_select.group').toString()}
              placeholder={translations('select').toString()}
              options={documentTypes.map((documentType) => {
                const result: IIdAndLabel = {
                  id: documentType.attachment_type_id,
                  label: translations(
                    `label.attachment_description_${documentType.attachment_type_description
                      .replace(/ /g, '_')
                      .toLowerCase()}`
                  ),
                };
                return result;
              })}
              value={selectedDocumentType}
              onChange={(e: any) => handleDropDown(e.target.value)}
              labelColor="#8094ae"
            />
          </div>
          <PrimaryButtonV2
            disabled={documentTitle === '' || !selectedDocumentType}
            text={translations('save.recording')}
            containerStyle={{ width: '100%' }}
            buttonStyle={{ width: '100%', padding: '20px 0px' }}
            onClick={handleSubmit}
          />
        </>
      )}
      {status === 'stopped' && (
        <div
          className={classes.retake}
          onClick={() => {
            clearTranscript();
            clearRecording();
            clearTranscript();
          }}
        >
          {translations('retake.recording')}
        </div>
      )}
    </div>
  );
};

export { VoiceRecorder };
